import React, { useEffect, useMemo, useState } from 'react';
import {
  SmsProviderDto,
  SmsProviderPayload,
  SmsProviderType,
  createSmsProvider,
  deleteSmsProvider,
  getSmsProviders,
  testSmsProvider,
  updateSmsProvider
} from '../../services/SmsProviderService';

const PROVIDER_TYPES: SmsProviderType[] = ['econet', 'kannel'];

const KANNEL_DEFAULT_URL =
  'http://HOST/sendsms?username={username}&password={password}&smsc={smsc}&from={from}&dlr-mask=31&to={to}&text={text}&dlr-url={dlr-url}';

interface FormState {
  name: string;
  type: SmsProviderType;
  apiUrl: string;
  apiKey: string;
  senderField: string;
  username: string;
  smsc: string;
  maxBatchSize: string;
  priority: string;
  isActive: boolean;
  isDefault: boolean;
  prefixes: string[];
  prefixDraft: string;
}

const emptyForm = (): FormState => ({
  name: '',
  type: 'econet',
  apiUrl: '',
  apiKey: '',
  senderField: '',
  username: '',
  smsc: '',
  maxBatchSize: '20',
  priority: '100',
  isActive: true,
  isDefault: false,
  prefixes: [],
  prefixDraft: ''
});

function formFromProvider(provider: SmsProviderDto): FormState {
  const config = provider.config || {};
  return {
    name: provider.name,
    type: provider.type,
    apiUrl: provider.apiUrl,
    apiKey: '',
    senderField: provider.senderField || '',
    username: provider.username || String(config.username || ''),
    smsc: provider.smsc || String(config.smsc || ''),
    maxBatchSize: String(provider.maxBatchSize || 1),
    priority: String(provider.priority || 100),
    isActive: provider.isActive,
    isDefault: provider.isDefault,
    prefixes: [...(provider.prefixes || [])],
    prefixDraft: ''
  };
}

const SmsProvidersPage: React.FC = () => {
  const [providers, setProviders] = useState<SmsProviderDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<SmsProviderDto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);

  const [testOpen, setTestOpen] = useState(false);
  const [testProvider, setTestProvider] = useState<SmsProviderDto | null>(null);
  const [testPhone, setTestPhone] = useState('');
  const [testSenderId, setTestSenderId] = useState('');
  const [testBody, setTestBody] = useState('');
  const [testing, setTesting] = useState(false);

  const [modal, setModal] = useState<{
    type: 'success' | 'error';
    title: string;
    message: string;
  } | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return providers;
    return providers.filter((provider) => {
      return (
        provider.name.toLowerCase().includes(q) ||
        provider.type.toLowerCase().includes(q) ||
        provider.apiUrl.toLowerCase().includes(q) ||
        provider.prefixes.some((prefix) => prefix.includes(q))
      );
    });
  }, [providers, query]);

  const loadProviders = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getSmsProviders();
      setProviders(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load SMS providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProviders();
  }, []);

  const addPrefix = () => {
    const value = form.prefixDraft.replace(/[\s\-\(\)+]/g, '');
    if (!value) return;
    if (!/^\d{3,15}$/.test(value)) {
      setModal({
        type: 'error',
        title: 'Invalid prefix',
        message: 'Prefixes must be 3-15 digits (e.g. 26377).'
      });
      return;
    }
    if (form.prefixes.includes(value)) {
      setForm((prev) => ({ ...prev, prefixDraft: '' }));
      return;
    }
    setForm((prev) => ({
      ...prev,
      prefixes: [...prev.prefixes, value],
      prefixDraft: ''
    }));
  };

  const removePrefix = (prefix: string) => {
    setForm((prev) => ({
      ...prev,
      prefixes: prev.prefixes.filter((item) => item !== prefix)
    }));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (provider: SmsProviderDto) => {
    setEditing(provider);
    setForm(formFromProvider(provider));
    setFormOpen(true);
  };

  const submitForm = async () => {
    if (!form.name.trim() || !form.apiUrl.trim()) {
      setModal({ type: 'error', title: 'Missing fields', message: 'Name and API URL are required.' });
      return;
    }
    if (!editing && form.type !== 'kannel' && !form.apiKey.trim()) {
      setModal({ type: 'error', title: 'API key required', message: 'An API key is required when creating a provider.' });
      return;
    }
    if (!editing && form.type === 'kannel' && !form.apiKey.trim()) {
      setModal({ type: 'error', title: 'Password required', message: 'A Kannel password is required when creating this provider.' });
      return;
    }

    const payload: SmsProviderPayload = {
      name: form.name.trim(),
      type: form.type,
      apiUrl: form.apiUrl.trim(),
      senderField: form.senderField.trim() || null,
      maxBatchSize: Math.max(1, Number(form.maxBatchSize) || 1),
      priority: Number(form.priority) || 100,
      isActive: form.isActive,
      isDefault: form.isDefault,
      prefixes: form.prefixes,
      username: form.username.trim(),
      smsc: form.smsc.trim()
    };
    if (form.apiKey.trim()) {
      payload.apiKey = form.apiKey.trim();
    }

    setSaving(true);
    try {
      if (editing) {
        const updated = await updateSmsProvider(editing.id, payload);
        setProviders((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
        setModal({ type: 'success', title: 'Provider updated', message: `${updated.name} was saved.` });
      } else {
        const created = await createSmsProvider(payload);
        setProviders((prev) => [created, ...prev]);
        setModal({ type: 'success', title: 'Provider created', message: `${created.name} was added.` });
      }
      setFormOpen(false);
    } catch (e: any) {
      setModal({ type: 'error', title: 'Save failed', message: e.message || 'Failed to save SMS provider' });
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (provider: SmsProviderDto) => {
    try {
      const updated = await updateSmsProvider(provider.id, { isActive: !provider.isActive });
      setProviders((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    } catch (e: any) {
      setModal({ type: 'error', title: 'Update failed', message: e.message || 'Failed to update provider' });
    }
  };

  const removeProvider = async (provider: SmsProviderDto) => {
    if (!window.confirm(`Delete ${provider.name}? Prefix routing for this provider will be removed.`)) return;
    try {
      await deleteSmsProvider(provider.id);
      setProviders((prev) => prev.filter((item) => item.id !== provider.id));
    } catch (e: any) {
      setModal({ type: 'error', title: 'Delete failed', message: e.message || 'Failed to delete provider' });
    }
  };

  const openTest = (provider: SmsProviderDto) => {
    setTestProvider(provider);
    setTestPhone('');
    setTestSenderId('');
    setTestBody(`Test SMS via ${provider.name}`);
    setTestOpen(true);
  };

  const submitTest = async () => {
    if (!testProvider) return;
    if (!testPhone.trim() || !testSenderId.trim()) {
      setModal({ type: 'error', title: 'Missing fields', message: 'Phone number and Sender ID are required.' });
      return;
    }
    setTesting(true);
    try {
      const result = await testSmsProvider(testProvider.id, {
        phone: testPhone.trim(),
        senderId: testSenderId.trim(),
        body: testBody.trim() || undefined
      });
      setTestOpen(false);
      if (result.success) {
        setModal({
          type: 'success',
          title: 'Test SMS sent',
          message: result.providerMessageId
            ? `Provider accepted the message (id: ${result.providerMessageId}).`
            : 'Provider accepted the test message.'
        });
      } else {
        setModal({
          type: 'error',
          title: 'Test SMS failed',
          message: result.error || 'The provider rejected the test message.'
        });
      }
    } catch (e: any) {
      setModal({ type: 'error', title: 'Test SMS failed', message: e.message || 'Failed to send test SMS' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-medium mb-6">SMS Providers</h1>

      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium">Configured gateways</h2>
          <div className="flex items-center gap-2">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              className="border rounded-md px-3 py-1.5 text-sm"
            />
            <button onClick={loadProviders} className="px-3 py-1.5 text-sm border rounded-md">
              Refresh
            </button>
            <button
              onClick={openCreate}
              className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90"
            >
              Add Provider
            </button>
          </div>
        </div>

        {loading && <div className="text-sm text-gray-500">Loading...</div>}
        {!loading && error && <div className="text-sm text-red-600">{error}</div>}

        {!loading && !error && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Endpoint</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prefixes</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API Key</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map((provider) => (
                  <tr key={provider.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="font-medium text-gray-900">{provider.name}</div>
                      {provider.isDefault && (
                        <div className="text-xs text-gray-500">Default fallback</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap capitalize">{provider.type}</td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={provider.apiUrl}>
                      {provider.apiUrl}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {provider.prefixes.length ? provider.prefixes.map((prefix) => (
                          <span key={prefix} className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded-full">
                            {prefix}
                          </span>
                        )) : (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-mono text-sm text-gray-500">
                      {provider.apiKeyMasked || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => toggleActive(provider)}
                        className={`px-2 py-1 rounded-full text-xs ${
                          provider.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {provider.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button onClick={() => openEdit(provider)} className="text-blue-600 hover:text-blue-800 mr-3">
                        Edit
                      </button>
                      <button onClick={() => openTest(provider)} className="text-amber-600 hover:text-amber-800 mr-3">
                        Test
                      </button>
                      <button onClick={() => removeProvider(provider)} className="text-red-600 hover:text-red-800">
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-8 text-center text-sm text-gray-500">
                      No SMS providers configured yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {formOpen && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-md w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-medium mb-4">{editing ? 'Edit SMS Provider' : 'Add SMS Provider'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="Econet Bulk"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                <select
                  value={form.type}
                  onChange={(e) => {
                    const nextType = e.target.value as SmsProviderType;
                    setForm((prev) => ({
                      ...prev,
                      type: nextType,
                      maxBatchSize: nextType === 'kannel' ? '1' : (prev.type === 'kannel' ? '20' : prev.maxBatchSize),
                      apiUrl: nextType === 'kannel' && !prev.apiUrl.includes('{to}')
                        ? KANNEL_DEFAULT_URL
                        : prev.apiUrl
                    }));
                  }}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                >
                  {PROVIDER_TYPES.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {form.type === 'kannel' ? 'Send URL template' : 'API URL'}
                </label>
                <input
                  value={form.apiUrl}
                  onChange={(e) => setForm((prev) => ({ ...prev, apiUrl: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 font-mono text-sm"
                  placeholder={form.type === 'kannel' ? KANNEL_DEFAULT_URL : 'https://...'}
                />
                {form.type === 'kannel' && (
                  <p className="mt-1 text-xs text-gray-500">
                    Placeholders: {'{username}'} {'{password}'} {'{smsc}'} {'{from}'} {'{to}'} {'{text}'} {'{dlr-url}'} {'{sender}'} {'{msgid}'}.
                    Values are URL-encoded when sending. Replace HOST with the Kannel base URL.
                  </p>
                )}
              </div>
              {form.type === 'kannel' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                      <input
                        value={form.username}
                        onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder="Kannel username"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">SMSC</label>
                      <input
                        value={form.smsc}
                        onChange={(e) => setForm((prev) => ({ ...prev, smsc: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder="e.g. netone"
                      />
                    </div>
                  </div>
                  <div className="rounded-md bg-gray-50 p-3 text-xs text-gray-600">
                    DLR callback (sent as {'{dlr-url}'}):
                    <div className="mt-1 font-mono break-all">
                      {(editing?.dlrCallbackUrl || '/api/sms/webhook/kannel') + '?msgid={msgid}&mobile=%p&sender=%F&dlrtype=%d'}
                    </div>
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {form.type === 'kannel' ? 'Password' : 'API Key'} {editing ? '(leave blank to keep current)' : ''}
                </label>
                <input
                  type="password"
                  value={form.apiKey}
                  onChange={(e) => setForm((prev) => ({ ...prev, apiKey: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder={editing ? '••••' : 'Enter API key'}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Max batch size</label>
                  <input
                    type="number"
                    min={1}
                    value={form.maxBatchSize}
                    onChange={(e) => setForm((prev) => ({ ...prev, maxBatchSize: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <input
                    type="number"
                    value={form.priority}
                    onChange={(e) => setForm((prev) => ({ ...prev, priority: e.target.value }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Number prefixes</label>
                <div className="flex gap-2">
                  <input
                    value={form.prefixDraft}
                    onChange={(e) => setForm((prev) => ({ ...prev, prefixDraft: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault();
                        addPrefix();
                      }
                    }}
                    placeholder="e.g. 26377"
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                  />
                  <button
                    type="button"
                    onClick={addPrefix}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {form.prefixes.map((prefix) => (
                    <span key={prefix} className="bg-gray-100 text-gray-700 text-sm px-2 py-1 rounded-full flex items-center gap-1">
                      {prefix}
                      <button type="button" onClick={() => removePrefix(prefix)} className="text-gray-400 hover:text-red-500">
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    className="h-4 w-4 text-[#FF5722] border-gray-300 rounded"
                  />
                  Active
                </label>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={form.isDefault}
                    onChange={(e) => setForm((prev) => ({ ...prev, isDefault: e.target.checked }))}
                    className="h-4 w-4 text-[#FF5722] border-gray-300 rounded"
                  />
                  Default fallback
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setFormOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={submitForm}
                disabled={saving}
                className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 disabled:opacity-50"
              >
                {saving ? 'Saving...' : editing ? 'Save' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {testOpen && testProvider && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-md w-full max-w-md p-6">
            <h3 className="text-lg font-medium mb-4">Test {testProvider.name}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone number</label>
                <input
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="263771234567"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sender ID</label>
                <input
                  value={testSenderId}
                  onChange={(e) => setTestSenderId(e.target.value)}
                  placeholder="MYBRAND"
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message (optional)</label>
                <textarea
                  value={testBody}
                  onChange={(e) => setTestBody(e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setTestOpen(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={submitTest}
                disabled={testing}
                className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#FF5722]/90 disabled:opacity-50"
              >
                {testing ? 'Sending...' : 'Send test'}
              </button>
            </div>
          </div>
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white shadow-xl">
            <div className="flex flex-col items-center px-6 py-6 text-center">
              <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-full ${modal.type === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
                {modal.type === 'success' ? (
                  <svg className="h-6 w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  </svg>
                )}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{modal.title}</h3>
              <p className="mt-2 text-sm text-gray-600">{modal.message}</p>
            </div>
            <div className="flex justify-center border-t border-gray-100 px-6 py-4">
              <button
                onClick={() => setModal(null)}
                className="rounded-md bg-[#FF5722] px-6 py-2 text-sm font-medium text-white hover:bg-[#F4511E]"
              >
                {modal.type === 'success' ? 'Done' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmsProvidersPage;
